import { useCallback, useRef, useState } from "react";
import { isSimpleEmailTemplate } from "../simpleEmailTemplate";

export type EditorMode = "builder" | "easy" | "html";

/**
 * Manages editor mode selection and bridges the builder's live content back to
 * the dialog's `content` state.
 *
 * - Defaults to "builder" when the content is Easy-Mode markup (starts with [[WL-EMAIL]]).
 * - Falls back to "html" (TinyMCE) otherwise.
 * - Exposes `missingVarNames` so the dialog Save gate can block when required vars
 *   are absent in builder mode.
 */
export function useBuilderBridge(initialContent: string | undefined) {
	const [mode, setMode] = useState<EditorMode>(() => (isSimpleEmailTemplate(initialContent) ? "builder" : "html"));

	// Stores missing required var names surfaced by EmailBuilder via onMissing.
	const [missingVarNames, setMissingVarNames] = useState<string[]>([]);
	const missingRef = useRef<string[]>([]);

	const handleBuilderChange = useCallback((content: string, missing: string[]) => {
		missingRef.current = missing;
		setMissingVarNames(missing);
		return content;
	}, []);

	// Reset mode when the dialog opens with new content.
	const resetMode = useCallback((content: string | undefined) => {
		setMode(isSimpleEmailTemplate(content) ? "builder" : "html");
		setMissingVarNames([]);
		missingRef.current = [];
	}, []);

	return {
		mode,
		setMode,
		missingVarNames,
		missingRef,
		handleBuilderChange,
		resetMode,
	};
}
